/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState } from 'react';
import { Button } from '../../atoms/button';
import { useDispatch, useSelector } from 'react-redux';
import { CustomHeadline } from '../../atoms/custom-headline';
import {
  ENTER_OTP,
  MOBILE_OTP_HEADLINE,
  QUILL_EMPTY_STRING,
  UPDATE_SIGN_IN,
  UPDATE_SINGLE_SIGN_ON,
  VERIFY_SIGN_IN_BUTTON,
  SIGN_IN_SUCCESS
} from '../../../../../constants';
import { ErrorMessage } from '../../atoms/error-message';
import {
  getErrorStyle,
  triggerLogin,
  genericError,
  triggerSSO,
  triggerUpdateWithOtp,
  inputFieldError,
  retrieveNumbers,
  handleScrollToTop
} from '../../util';
import get from 'lodash/get';
import './otp-screen.module.css';
import { ResendOtp } from '../../atoms/resend-otp';
import { AgreementText } from '../../atoms/agreement-text';
import { checkIfSingleSignOn } from '../../sso-utils';
import { SuccessMessage } from '../../atoms/success-message';

export const MobileOtpScreen = ({ mode, isUserLoginPage = false, displayData = '' }) => {
  const { general = {}, login = {}, sessionLimit={} } = useSelector(state => get(state, ['qt', 'config', 'pagebuilder-config'], {}));

  const { currentPath = '', currentHostUrl = '' } = useSelector(state => get(state, ['qt'], {}));
  const { button, enterOtpLabel, enterOtpTitle, resendOtpInLabel, resendOtpLabel, agreementText } = get(
    login,
    ['mobile-otp', 'rows', 0, 'config'],
    {}
  );
  const { loginSuccess = '' } = get(login, ['pageSetting'], {});
  const { sso = {} } = general;
  const [otpValue, setOtpValue] = useState('');
  const [response, setResponse] = useState(null);
  const [api, setApi] = useState(null);
  const [displaySuccess, setSuccess] = useState(false);
  const [disableButton, setDisableButton] = useState(false)
  const dispatch = useDispatch();
  const { signIn = {}, singleSignOn = {} } = useSelector(state => get(state, ['login'], {}));
  const {
    inputData = '',
    countryCode = '',
    countryIso = '',
    qtAuthCookie = '',
    updateOtp = false
  } = get(signIn, ['userData'], {});
  const {
    inputData: ssoInputData = '',
    countryCode: ssoCountryCode = '',
    countryIso: ssoCountryIso = '',
    qtAuthCookie: ssoQtAuthCookie = '',
    updateOtp: ssoUpdateOtp = false
  } = get(singleSignOn, ['userData'], {});

  const wrapperStyle = displaySuccess ? `auth--wrapper ${mode} success-wrapper` : `auth--wrapper ${mode}`;
  const data = !isUserLoginPage ? `${countryCode}${inputData}` : `${ssoCountryCode}${ssoInputData}`;
  const authCookie = !isUserLoginPage ? qtAuthCookie : ssoQtAuthCookie;
  const isUpdateOtp = !isUserLoginPage ? updateOtp : ssoUpdateOtp;
  const updatedCountryIso = !isUserLoginPage ? countryIso : ssoCountryIso;
  const sketchesHost = useSelector(state => get(state, ['qt', 'config', 'sketches-host'], ''));
  const triggerOnEdit = () =>
    dispatch({ type: !isUserLoginPage ? UPDATE_SIGN_IN : UPDATE_SINGLE_SIGN_ON, payload: { currentScreen: 'signIn' } });

  handleScrollToTop();

  const triggerHandler = async () => {
    const onSuccess = () => {
      global?.localStorage?.removeItem('userEmail');
      setSuccess(true);
      setDisableButton(true)
    };

    if (isUpdateOtp) {
      const updateOtpProps = { otp: otpValue, email: data, authCookie: authCookie, sketchesHost };
      const updateWithOtpResponse = await triggerUpdateWithOtp(updateOtpProps);
      setResponse(updateWithOtpResponse);
      setApi('update-with-otp');
      if (updateWithOtpResponse === 200) onSuccess();
    } else {
      const loginProps = { data: data, credential: otpValue, type: 'otp', sketchesHost };
      const { responseStatus: otpResponseStatus } = await triggerLogin(loginProps);
      setResponse(otpResponseStatus);
      setApi('login-mobile');
      if (otpResponseStatus === 200) {
        if (isUserLoginPage && checkIfSingleSignOn(sso) && window?.location) {
          const ssoProps = { sso: sso, currentHostUrl: currentHostUrl, currentPath: currentPath, sketchesHost };
          triggerSSO(ssoProps);
        }
        onSuccess();
      }
    }
  };

  return (
    <div className={wrapperStyle}>
      <CustomHeadline
        headline={enterOtpTitle && enterOtpTitle !== QUILL_EMPTY_STRING ? enterOtpTitle : MOBILE_OTP_HEADLINE}
        editData={displayData || data}
        onEdit={triggerOnEdit}
        id='mobile-otp-headline'
      />
      <div className={getErrorStyle(response, api, mode)} styleName='input-wrapper'>
        <label for='otp'>{enterOtpLabel || ENTER_OTP}</label>
        <input
          inputmode='numeric'
          pattern='[0-9]*'
          value={otpValue}
          id='otp'
          onChange={event => {
            setResponse(null);
            setApi(null);
            setOtpValue(retrieveNumbers(event.target.value));
          }}
          name='otp'
          required
        />
        {inputFieldError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
        {isUpdateOtp ? (
          <ResendOtp
            resendOtpLabel={resendOtpLabel}
            resendOtpInLabel={resendOtpInLabel}
            setResponse={setResponse}
            setApi={setApi}
            email={data}
          />
        ) : (
          <ResendOtp
            resendOtpLabel={resendOtpLabel}
            resendOtpInLabel={resendOtpInLabel}
            setResponse={setResponse}
            setApi={setApi}
            phoneNumber={data}
            countryIso={updatedCountryIso}
          />
        )}
      </div>
      <Button
        text={button || VERIFY_SIGN_IN_BUTTON}
        clickHandler={() => triggerHandler()}
        disable={!otpValue || disableButton}
        enableEnterClick={true}
        enableLoader={true}
      />
      {genericError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
      {!isUpdateOtp ? <AgreementText currentScreen='otp' customAgreementText={agreementText} /> : <></>}
      {displaySuccess ? (
        <SuccessMessage message={loginSuccess || SIGN_IN_SUCCESS} mode={mode} isUserLoginPage={isUserLoginPage} sessionLimit={sessionLimit}/>
      ) : (
        <></>
      )}
    </div>
  );
};
